import { Box, Typography, LinearProgress, Tooltip } from '@mui/material';
import { Battery20, Battery50, Battery60, Battery80, BatteryAlert } from '@mui/icons-material';
import {
  getBatteryLevel,
  getBatteryColor,
  getBatteryLevelLabel,
  formatBatteryPercentage,
} from '../../utils/battery';
import { useTheme } from '../../hooks/useTheme';
import { BatteryPolicy } from '../../types/drone';

interface BatteryStatusProps {
  percentage: number | null;
  voltage: number | null;
  isHealthy: boolean;
  /**
   * What this aircraft lands itself at (#629). Absent from a backend that does
   * not send one — which is not "no threshold", only "not told", so the display
   * says so rather than implying the aircraft will fly until it falls.
   */
  landingPolicy?: BatteryPolicy;
}

export const BatteryStatus: React.FC<BatteryStatusProps> = ({
  percentage,
  voltage,
  isHealthy,
  landingPolicy,
}) => {
  const { isDarkMode } = useTheme();
  const level = getBatteryLevel(percentage);
  const color = getBatteryColor(level, isDarkMode);
  const levelLabel = getBatteryLevelLabel(level);

  const getBatteryIcon = () => {
    if (level === 'unknown') {
      return <BatteryAlert sx={{ color }} titleAccess={levelLabel} />;
    }
    if (level === 'red') {
      return <Battery20 sx={{ color }} titleAccess={levelLabel} />;
    }
    if (level === 'amber') {
      return <Battery50 sx={{ color }} titleAccess={levelLabel} />;
    }
    if (level === 'normal') {
      return <Battery60 sx={{ color }} titleAccess={levelLabel} />;
    }
    return <Battery80 sx={{ color }} titleAccess={levelLabel} />;
  };

  // Where this aircraft's own limit sits on the bar.
  //
  // Drawn only when the server gave us a percentage for the threshold, which it
  // withholds for a chemistry with no measured discharge curve. Computing one
  // here from the volts would mean this file carrying a discharge curve, which is
  // the fifth copy of a rule #629 exists to collapse.
  const thresholdPercent = landingPolicy?.thresholdPercent;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {getBatteryIcon()}
        <Typography variant="body2" fontWeight="bold">
          Battery
        </Typography>
      </Box>
      <Box sx={{ pl: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="h6" sx={{ color }}>
            {formatBatteryPercentage(percentage)}
          </Typography>
          {/* The word, not only the colour (WCAG AA). An operator with a colour
              vision deficiency, or a washed-out screen in sunlight at a field
              test, reads the same thing. */}
          <Typography variant="caption" sx={{ color }}>
            {levelLabel}
          </Typography>
        </Box>

        {percentage !== null && (
          <Box sx={{ position: 'relative', mt: 1 }}>
            <LinearProgress
              variant="determinate"
              value={percentage}
              aria-label={`Battery ${formatBatteryPercentage(percentage)}, ${levelLabel}`}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'action.hover',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: color,
                },
              }}
            />
            {/* The aircraft's own limit, marked on the bar.
                
                Because the colour bands are fixed and this number is not, the bar
                alone cannot say how close the aircraft is to putting itself down:
                a drone set to 10 % sitting at 16 % looks merely amber when it is
                six points away, and one set to 30 % looks the same shade while
                already descending (#629). */}
            {thresholdPercent !== undefined && (
              <Tooltip
                title={`This drone lands itself below ${thresholdPercent.toFixed(0)}% (${landingPolicy?.thresholdVoltsPerCell.toFixed(2)} V per cell)`}
              >
                <Box
                  data-testid="landing-threshold-marker"
                  sx={{
                    position: 'absolute',
                    top: -2,
                    left: `${Math.min(100, Math.max(0, thresholdPercent))}%`,
                    width: 2,
                    height: 12,
                    backgroundColor: 'text.primary',
                  }}
                />
              </Tooltip>
            )}
          </Box>
        )}

        {voltage !== null && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {voltage.toFixed(2)}V {!isHealthy && '(Unhealthy)'}
          </Typography>
        )}

        {/* Said in words as well as marked on the bar, because the marker is a
            two-pixel line and the number is the thing an operator needs to
            compare against the reading above. */}
        {landingPolicy && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: 'block' }}
          >
            Lands itself below {landingPolicy.thresholdVoltsPerCell.toFixed(2)} V/cell
            {thresholdPercent !== undefined && ` (~${thresholdPercent.toFixed(0)}%)`}
            {landingPolicy.isDefault && ' — fleet default'}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
